// @ts-nocheck
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonUI, Switch, showNotification, useQueryFetch } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { getContextualReportURL, getLastContextualReport } from '@/modules/reporting/reporting.resources';
import {
  cleanPdfStatusByKey,
  setContextualDiscoveryReportStatusChanged,
  setCoverageStudyReportStatusChanged,
  setGeoLookALikeStatusChanged
} from '@/store/actions/reporting.actions';
import {
  getContextualDiscoveryReportStatus,
  getCoverageStudyReportStatus,
  getGeoLookALikeStatus,
  getPdfReportStatus,
  getSelectedCampaigns
} from '@/store/selectors/reporting.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { getGeoLookALikeReportUrl, getLastGeoLookALikeReport } from '@/ts-resources/reporting/geoLookALike.resources';
import { getLastPdfReport, getPdfReportUrl } from '@/ts-resources/reporting/pdfReport.resources';
import { getCoverageStudyReportURL, getLastCoverageReport } from '../CoverageStudy/resources';

import type { PdfReportInterface } from 'sonata-static-storage/lib/schemas/pdfReport/pdfReport.schema';

export default function LastDownloads({ id }: { id: string }) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const geoLookALikeReportStore = useSelector(getGeoLookALikeStatus);
  const contextualDiscoveryReportStore = useSelector(getContextualDiscoveryReportStatus);
  const coverageStudyReportStore = useSelector(getCoverageStudyReportStatus);
  const pdfReportStore: Record<PdfReportInterface['type'], PdfReportInterface> = useSelector(getPdfReportStatus);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const campaigns = useSelector(getSelectedCampaigns);
  const campaignIds = campaigns.map((c: Record<string, any>) => c.id);

  const [prevContextualDiscoveryReportStore, setPrevContextualDiscoveryReportStore] =
    useState(contextualDiscoveryReportStore);
  const [prevGeoLookALikeReportStore, setPrevGeoLookALikeReportStore] = useState(geoLookALikeReportStore);
  const [prevCoverageStudyReportStore, setPrevCoverageStudyReportStore] = useState(coverageStudyReportStore);

  const sonataPDFPermission = checkUserResourcePermissions('reportingDownloadPdf', user, userRoles);
  const contextualPDFPermission = checkUserResourcePermissions(
    'reportingDownloadPdfContextualDiscovery',
    user,
    userRoles
  );

  const lastPdfReportToken = {};
  const { data: lastPdfReport, refetch: refetchPdfReport } = useQueryFetch(
    ['lastPdfReport', user.id, campaignIds],
    async () => {
      try {
        return getLastPdfReport(
          user.id!,
          campaignIds,
          'reporting-base-template-with-subtemplateId',
          lastPdfReportToken
        );
      } catch {
        showNotification(
          'alert',
          formatMessage({
            defaultMessage: 'There was an error while preparing the report.',
            id: 'reporting.header.buttons.pdfReportErrorMsg'
          })
        );
      }
    },
    {
      staleTime: 0
    }
  );

  const lastGeoLookALikeReportToken = {};
  const { data: lastGeoLookALikeReportStorage, refetch: refetchGeoLookALikeReport } = useQueryFetch(
    ['lastGeoLookALikeReport', user.id, campaignIds],
    async () => {
      try {
        return getLastGeoLookALikeReport(user.id, campaignIds, 'reporting', lastGeoLookALikeReportToken);
      } catch {
        showNotification(
          'alert',
          formatMessage({
            defaultMessage: 'There was an error while preparing the report.',
            id: 'reporting.header.buttons.contextualReportErrorMsg'
          })
        );
      }
    },
    {
      staleTime: 0
    }
  );

  const { data: lastContextualDiscoveryReportStorage, refetch: refetchContextualDiscovertReport } = useQueryFetch(
    ['lastContextualDiscoveryReport', user.id, campaignIds],
    async () => {
      try {
        return getLastContextualReport(user.id, campaignIds);
      } catch {
        showNotification(
          'alert',
          formatMessage({
            defaultMessage: 'There was an error while preparing the report.',
            id: 'reporting.header.buttons.contextualReportErrorMsg'
          })
        );
      }
    },
    {
      staleTime: 0
    }
  );

  const lastCoverageStudyReportToken = {};
  const { data: lastCoverageStudyReportStorage, refetch: refetchCoverageStudyReport } = useQueryFetch(
    ['lastCoverageStudyReport', user.id, campaignIds],
    async () => {
      try {
        return getLastCoverageReport(user.id, campaignIds[0], lastCoverageStudyReportToken);
      } catch {
        showNotification(
          'alert',
          formatMessage({
            defaultMessage: 'There was an error while preparing the report.',
            id: 'reporting.header.buttons.contextualReportErrorMsg'
          })
        );
      }
    }
  );

  const contextualDiscoveryReportUrlToken = {};
  const handleLastContextualDiscoveryDownloadClick = async () => {
    try {
      const fileUrl = await getContextualReportURL(
        lastContextualDiscoveryReport.reportId,
        contextualDiscoveryReportUrlToken
      );
      window.open(fileUrl, '_blank');
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: off
      console.error(e);
      showNotification(
        'alert',
        formatMessage({
          defaultMessage: 'There was an error while preparing the report.',
          id: 'reporting.header.buttons.contextualReportErrorMsg'
        })
      );
    }
  };

  const geoLookALikeReportUrlToken = {};
  const handleLastGeoLookALikeDownloadClick = async () => {
    try {
      const fileUrl = await getGeoLookALikeReportUrl(lastGeoLookALikeReport.reportId, geoLookALikeReportUrlToken);
      window.open(fileUrl, '_blank');
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: off
      console.error(e);
      showNotification(
        'alert',
        formatMessage({
          defaultMessage: 'There was an error while preparing the report.',
          id: 'reporting.header.buttons.contextualReportErrorMsg'
        })
      );
    }
  };

  const pdfReportUrlToken = {};
  const handleLastPdfDownloadClick = async (currentPdfReportId: string) => {
    const pdfReportId = currentPdfReportId || lastPdfReport?._id;

    try {
      const result = await getPdfReportUrl(pdfReportId, user.id, pdfReportUrlToken);
      window.open(result.publicUrl, '_blank');
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: off
      console.error(e);
      showNotification(
        'alert',
        formatMessage({
          defaultMessage: 'There was an error while preparing the report.',
          id: 'reporting.header.buttons.contextualReportErrorMsg'
        })
      );
    }
  };

  const coverageStudyReportUrlToken = {};
  const handleLastCoverageStudyDownloadClick = async () => {
    try {
      const fileUrl = await getCoverageStudyReportURL(lastCoverageStudyReport.reportId, coverageStudyReportUrlToken);
      window.open(fileUrl, '_blank');
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: off
      console.error(e);
      showNotification(
        'alert',
        formatMessage({
          defaultMessage: 'There was an error while preparing the report.',
          id: 'reporting.header.buttons.contextualReportErrorMsg'
        })
      );
    }
  };

  const lastGeoLookALikeReport =
    geoLookALikeReportStore?.status === 'success' ? geoLookALikeReportStore : lastGeoLookALikeReportStorage;
  const lastCoverageStudyReport =
    coverageStudyReportStore?.status === 'success' ? coverageStudyReportStore : lastCoverageStudyReportStorage;
  const lastContextualDiscoveryReport =
    contextualDiscoveryReportStore?.status === 'success'
      ? contextualDiscoveryReportStore
      : lastContextualDiscoveryReportStorage;

  if (!prevContextualDiscoveryReportStore?.reportUrl && contextualDiscoveryReportStore?.reportUrl) {
    setPrevContextualDiscoveryReportStore(contextualDiscoveryReportStore);
    refetchContextualDiscovertReport();
    dispatch(setContextualDiscoveryReportStatusChanged({}));
    handleLastContextualDiscoveryDownloadClick();
  }

  if (!prevGeoLookALikeReportStore?.fileUrl && geoLookALikeReportStore?.fileUrl) {
    setPrevGeoLookALikeReportStore(prevGeoLookALikeReportStore);
    refetchGeoLookALikeReport();
    dispatch(setGeoLookALikeStatusChanged({}));
    handleLastGeoLookALikeDownloadClick();
  }

  if (Object.keys(pdfReportStore)[0]) {
    Object.entries(pdfReportStore).forEach(([key, value]) => {
      if (value?.status === 'success' && value?.result?.publicUrl) {
        refetchPdfReport();
        dispatch(cleanPdfStatusByKey({ key }));
        handleLastPdfDownloadClick(value._id!);
      }
    });
  }

  if (!prevCoverageStudyReportStore?.reportUrl && coverageStudyReportStore?.reportUrl) {
    setPrevCoverageStudyReportStore(coverageStudyReportStore);
    refetchCoverageStudyReport();
    dispatch(setCoverageStudyReportStatusChanged({}));
    handleLastCoverageStudyDownloadClick();
  }

  if (!sonataPDFPermission && !contextualPDFPermission) {
    return null;
  }

  return (
    <Switch>
      <Switch.Case
        condition={[
          lastContextualDiscoveryReport?.status,
          lastCoverageStudyReport?.status,
          lastGeoLookALikeReport?.status,
          lastPdfReport?.status
        ].includes('success')}
      >
        <div className="reportingHeader__downloadButtonsLast">
          {lastContextualDiscoveryReport?.reportUrl ? (
            <ButtonUI
              badge={contextualDiscoveryReportStore?.status === 'success'}
              className="reportingHeader__downloadButtonsLastItem"
              icon="fas fa-file-arrow-down"
              id={`reportingHeader-downloadButtons-contextualReportLast-${id}`}
              key="reportingHeader-downloadButtons-contextualReportLast"
              onClick={handleLastContextualDiscoveryDownloadClick}
              shape="link"
              size="small"
              text={
                lastContextualDiscoveryReport?.name ||
                formatMessage({
                  defaultMessage: 'No name report',
                  id: 'reporting.header.buttons.contextualReportDefaultName'
                })
              }
              type="secondary"
            />
          ) : null}
          {lastGeoLookALikeReport?.fileUrl ? (
            <ButtonUI
              badge={geoLookALikeReportStore.status === 'success'}
              className="reportingHeader__downloadButtonsLastItem"
              icon="fas fa-file-arrow-down"
              id={`reportingHeader-downloadButtons-geoLookALikeReportLast-${id}`}
              key="-${id}reportingHeader-downloadButtons-geoLookALikeReportLast"
              onClick={handleLastGeoLookALikeDownloadClick}
              shape="link"
              size="small"
              text={
                lastGeoLookALikeReport?.name ||
                formatMessage({
                  defaultMessage: 'No name report',
                  id: 'reporting.header.buttons.contextualReportDefaultName'
                })
              }
              type="secondary"
            />
          ) : null}
          {lastPdfReport?.result ? (
            <ButtonUI
              badge={lastPdfReport.status === 'success'}
              className="reportingHeader__downloadButtonsLastItem"
              icon="fas fa-file-arrow-down"
              id={`reportingHeader-downloadButtons-pdfReportLast-${id}`}
              key="reportingHeader-downloadButtons-pdfReportLast"
              onClick={() => handleLastPdfDownloadClick(lastPdfReport?._id)}
              shape="link"
              size="small"
              text={
                lastPdfReport?.name ||
                formatMessage({
                  defaultMessage: 'No name report',
                  id: 'reporting.header.buttons.pdfReportDefaultName'
                })
              }
              type="secondary"
            />
          ) : null}
          {lastCoverageStudyReport?.status ? (
            <ButtonUI
              badge={lastCoverageStudyReport.status === 'success'}
              className="reportingHeader__downloadButtonsLastItem"
              icon="fas fa-file-arrow-down"
              id={`reportingHeader-downloadButtons-coverageStudyLast-${id}`}
              key="reportingHeader-downloadButtons-coverageStudyLast"
              onClick={handleLastCoverageStudyDownloadClick}
              shape="link"
              size="small"
              text={
                lastCoverageStudyReport?.name ||
                formatMessage({
                  defaultMessage: 'No name report',
                  id: 'reporting.header.buttons.pdfReportDefaultName'
                })
              }
              type="secondary"
            />
          ) : null}
        </div>
      </Switch.Case>
    </Switch>
  );
}
